/**
 * 실제 네트워크 속도 측정 유틸리티
 *
 * Navigator.connection API가 부정확한 경우가 많아서
 * 실제 Ping 테스트로 네트워크 속도를 측정합니다.
 *
 * 사용처:
 * - 30초마다 백그라운드 체크
 * - 저장 버튼 클릭 시 실시간 측정
 * - 자동저장 시 실시간 측정
 */

const API_URL = process.env.REACT_APP_API_URL;

// 속도 측정 결과 캐싱
let cachedSpeed = null;
let lastTestTime = 0;
let lastResponseTime = 0; // 🆕 마지막 측정된 실제 응답 시간 (ms)
const CACHE_DURATION = 30000; // 30초 캐시

// 속도 기준 (밀리초)
const SPEED_THRESHOLDS = {
  FAST: 500,      // 500ms 이내 = 빠름 (4g/5g 수준) - 더 안전한 기준
  MEDIUM: 2000,   // 2초 이내 = 보통 (3g 수준)
  SLOW: 5000,     // 5초 이내 = 느림 (2g 수준)
  // 5초 이상 또는 실패 = 오프라인
};

/**
 * 속도 측정 결과 타입
 * @typedef {'fast' | 'medium' | 'slow' | 'offline'} SpeedType
 */

/**
 * 실제 네트워크 Ping 테스트
 * 서버에 HEAD 요청을 보내고 응답 시간을 측정
 *
 * @param {boolean} ignoreCache - 캐시 무시하고 강제 측정
 * @returns {Promise<SpeedType>}
 */
export async function measureNetworkSpeed(ignoreCache = false) {
  const now = Date.now();

  // 캐시 확인 (30초 이내)
  if (!ignoreCache && cachedSpeed && (now - lastTestTime) < CACHE_DURATION) {
    // console.log(`[SpeedTest] 캐시된 속도 사용: ${cachedSpeed} (${Math.round((now - lastTestTime) / 1000)}초 전 측정)`);
    return cachedSpeed;
  }

  // console.log('[SpeedTest] 실제 네트워크 속도 측정 시작...');

  try {
    const startTime = Date.now();

    // Ping 요청 (HEAD 메서드 = 최소 데이터)
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), SPEED_THRESHOLDS.SLOW);

    const response = await fetch(`${API_URL}/api/ping`, {
      method: 'HEAD',
      signal: controller.signal,
      cache: 'no-cache', // 캐시 사용하지 않음
      headers: {
        'Cache-Control': 'no-cache',
      },
    });

    clearTimeout(timeoutId);

    const responseTime = Date.now() - startTime;

    // 응답 시간으로 속도 판단
    let speed;
    if (responseTime < SPEED_THRESHOLDS.FAST) {
      speed = 'fast';
    } else if (responseTime < SPEED_THRESHOLDS.MEDIUM) {
      speed = 'medium';
    } else if (responseTime < SPEED_THRESHOLDS.SLOW) {
      speed = 'slow';
    } else {
      speed = 'offline';
    }

    // 캐싱 (🆕 응답 시간도 함께 저장)
    cachedSpeed = speed;
    lastTestTime = now;
    lastResponseTime = responseTime;

    // console.log(`[SpeedTest] 속도 측정 완료: ${speed} (응답시간: ${responseTime}ms)`);

    return speed;

  } catch (error) {
    // console.log('[SpeedTest] 속도 측정 실패 (오프라인):', error.message);

    // 에러 발생 = 오프라인
    cachedSpeed = 'offline';
    lastTestTime = now;
    lastResponseTime = 5000; // 타임아웃으로 간주

    return 'offline';
  }
}

/**
 * 오프라인 모드 사용 여부 판단
 *
 * @param {boolean} ignoreCache - 캐시 무시하고 강제 측정
 * @returns {Promise<boolean>} true = 오프라인 저장, false = 서버 저장
 */
export async function shouldUseOfflineMode(ignoreCache = false) {
  // 기본 온라인 체크
  if (!navigator.onLine) {
    // console.log('[SpeedTest] navigator.onLine = false, 오프라인 모드');
    return true;
  }

  // 실제 속도 측정
  const speed = await measureNetworkSpeed(ignoreCache);

  // 🔥 버그 수정: medium(500ms~2000ms)도 서버 저장 허용
  // - fast (0~500ms): 서버 저장 ✅
  // - medium (500ms~2000ms): 서버 저장 ✅ (모바일 4G/LTE 정상 범위)
  // - slow (2000ms~5000ms): 오프라인 저장
  // - offline (5000ms 이상): 오프라인 저장
  const useOffline = (speed === 'slow' || speed === 'offline');

  console.log(`[SpeedTest] 속도: ${speed} → ${useOffline ? '오프라인 저장' : '서버 저장'}`);

  return useOffline;
}

/**
 * 캐시 초기화
 * 테스트나 디버깅 시 사용
 */
export function clearSpeedCache() {
  cachedSpeed = null;
  lastTestTime = 0;
  lastResponseTime = 0;
  // console.log('[SpeedTest] 캐시 초기화됨');
}

/**
 * 현재 캐시된 속도 조회
 * @returns {SpeedType | null}
 */
export function getCachedSpeed() {
  return cachedSpeed;
}

/**
 * 마지막 측정 시간 조회
 * @returns {number} timestamp
 */
export function getLastTestTime() {
  return lastTestTime;
}

/**
 * 속도를 한글로 변환
 * @param {SpeedType} speed
 * @returns {string}
 */
export function getSpeedText(speed) {
  const speedTexts = {
    fast: '빠름 (4G/5G)',
    medium: '보통 (3G)',
    slow: '느림 (2G)',
    offline: '오프라인',
  };
  return speedTexts[speed] || '알 수 없음';
}

/**
 * Ping 응답 시간을 기반으로 네트워크 정보 추정
 * @param {number} responseTime - Ping 응답 시간 (밀리초)
 * @returns {Object} 추정된 네트워크 정보
 */
export function estimateNetworkInfo(responseTime) {
  let effectiveType = 'unknown';
  let estimatedDownlink = 0;
  let estimatedRtt = responseTime;

  // 응답 시간 기반 네트워크 타입 및 속도 추정
  if (responseTime < 50) {
    // 초고속 (5G/WiFi 6)
    effectiveType = '5g';
    estimatedDownlink = 100; // ~100 Mbps
  } else if (responseTime < 100) {
    // 매우 빠름 (5G/WiFi 5)
    effectiveType = '5g';
    estimatedDownlink = 50; // ~50 Mbps
  } else if (responseTime < 200) {
    // 빠름 (4G+)
    effectiveType = '4g';
    estimatedDownlink = 20; // ~20 Mbps
  } else if (responseTime < 500) {
    // 보통 (4G)
    effectiveType = '4g';
    estimatedDownlink = 10; // ~10 Mbps
  } else if (responseTime < 1000) {
    // 느림 (3G+)
    effectiveType = '3g';
    estimatedDownlink = 5; // ~5 Mbps
  } else if (responseTime < 2000) {
    // 매우 느림 (3G)
    effectiveType = '3g';
    estimatedDownlink = 1.5; // ~1.5 Mbps
  } else if (responseTime < 5000) {
    // 극도로 느림 (2G)
    effectiveType = '2g';
    estimatedDownlink = 0.5; // ~0.5 Mbps
  } else {
    // 오프라인/타임아웃
    effectiveType = 'slow-2g';
    estimatedDownlink = 0.1; // ~0.1 Mbps
  }

  return {
    effectiveType,
    downlink: estimatedDownlink,
    rtt: estimatedRtt
  };
}

/**
 * 현재 측정된 네트워크 정보 조회 (캐시된 값 기반)
 * @returns {Object} 네트워크 정보
 */
export function getCurrentNetworkInfo() {
  if (!cachedSpeed || !lastTestTime) {
    return {
      speed: 'unknown',
      effectiveType: 'unknown',
      downlink: 0,
      rtt: 0,
      lastTestTime: null
    };
  }

  // 🆕 실제 측정된 응답 시간 사용 (저장된 값이 있으면)
  const responseTime = lastResponseTime || 0;
  const networkInfo = estimateNetworkInfo(responseTime);

  return {
    speed: cachedSpeed,
    ...networkInfo,
    lastTestTime
  };
}

// 디버그용 전역 노출
if (process.env.NODE_ENV === 'development') {
  window.FDES_SPEED_TEST = {
    measure: measureNetworkSpeed,
    shouldUseOffline: shouldUseOfflineMode,
    clearCache: clearSpeedCache,
    getCache: getCachedSpeed,
    getLastTest: getLastTestTime,
  };
  // console.log('[SpeedTest] 디버그 도구 활성화: window.FDES_SPEED_TEST');
}

export default {
  measureNetworkSpeed,
  shouldUseOfflineMode,
  clearSpeedCache,
  getCachedSpeed,
  getLastTestTime,
  getSpeedText,
  estimateNetworkInfo,
  getCurrentNetworkInfo,
};
